import { Link } from "react-router-dom";
import { metaFor } from "../../shared/seo";
import { useMeta } from "../lib/seo";
import { origin } from "../lib/origin";

// A description of what this software actually does, written from the code
// rather than from a template. Every cookie and storage key named here exists
// in the repository; if one is removed, this page should change with it.
//
// It is deliberately not written in the voice of a finished legal policy.
// Promising things like "we never share your data with third parties" would be
// a commitment about how the business is run, which code cannot verify.

export function Privacy() {
  useMeta(metaFor({ route: "privacy", base: origin() }));

  return (
    <div className="narrow-lg">
      <h1>Privacy, cookies and analytics</h1>
      <p className="muted">
        What this site stores, what it sends elsewhere, and why. Written to be checkable against
        the running site rather than to cover us.
      </p>

      <div className="nfa section">
        This page describes current behaviour in plain language. It has not been reviewed by a
        lawyer and is not a substitute for a formal privacy policy, which this site still needs
        before it takes on users at scale.
      </div>

      <section className="section">
        <h2>What is public by design</h2>
        <p>
          Reviews are public. When you publish one, the review text, your rating, the
          verification badge it earned, and the display name or username on your account are
          visible to anyone, including search engines and AI crawlers. That is the point of the
          site — a review nobody can read cannot help anyone.
        </p>
        <p>
          Your email address is not shown on any public page. Wallet addresses used to prove
          usage are used to check the proof and are not published next to your review.
        </p>
      </section>

      <section className="section" id="cookies">
        <h2>Cookies this site sets</h2>
        <p>Three, all functional, none used for advertising:</p>
        <ul className="policy-list">
          <li>
            <code>cc_session</code> - keeps you signed in after you authenticate. Without it you
            would be signed out on every page load.
          </li>
          <li>
            <code>cc_oauth</code> - a short-lived value during sign-in that ties the response
            from the identity provider back to the request that started it. It is what stops
            someone else&rsquo;s sign-in being replayed into your browser.
          </li>
          <li>
            <code>cc_xchg</code> - the same protection for the exchange-connection flow used to
            verify that you really traded on a platform.
          </li>
        </ul>

        <h3>Stored in your browser, never sent to us</h3>
        <ul className="policy-list">
          <li><code>cc-theme</code> - light or dark.</li>
          <li><code>cc-compare</code> - the listings you have ticked for comparison.</li>
        </ul>
      </section>

      <section className="section">
        <h2>Analytics</h2>
        <p>
          This site loads Google Tag Manager, which in turn loads Google Analytics. Google
          receives your IP address, the pages you visit, and the usual browser and device
          details, and sets its own cookies (typically <code>_ga</code> and{" "}
          <code>_ga_&hellip;</code>) to recognise a returning browser. We use it to see which
          pages people actually read.
        </p>
        <p>
          <strong>There is no consent banner yet.</strong> Analytics currently loads for every
          visitor. If you are in a jurisdiction where that requires your prior consent, it is
          fair to say this site is not yet meeting that bar; a browser-level tracking blocker or
          Do Not Track extension will stop it in the meantime.
        </p>
      </section>

      <section className="section">
        <h2>Other services your browser contacts</h2>
        <ul className="policy-list">
          <li>
            <strong>Google Fonts</strong> - the two typefaces load from Google&rsquo;s servers,
            so Google sees your IP address on the first page you open.
          </li>
          <li>
            <strong>SSOJet</strong> - handles sign-in. You authenticate on their domain and this
            site receives an identity claim back, not your password.
          </li>
          <li>
            <strong>Cloudflare</strong> - serves the site and stores its database. Requests
            necessarily pass through their network.
          </li>
        </ul>
        <p>
          Market data and token facts come from public sources such as CoinGecko and chain
          explorers. Those requests are made by our servers, not your browser, so they never see
          you.
        </p>
      </section>

      <section className="section">
        <h2>What we do not do</h2>
        <p>
          No advertising networks, no third-party ad or retargeting pixels, and no sale of
          personal data. The site&rsquo;s content security policy is written to permit only the
          Google endpoints named above, so an unexpected tracker cannot quietly load. you can
          check that yourself in the response headers.
        </p>
      </section>

      <section className="section">
        <h2>Deleting your data</h2>
        <p>
          Ask and we will remove your account and reviews. There is no self-serve delete button
          yet; that is a gap, not a policy.
        </p>
      </section>

      <p className="section">
        <Link to="/methodology" className="btn btn-ghost btn-sm">How scoring works →</Link>
      </p>
    </div>
  );
}
